import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { PrinterX, Truck } from 'lucide-react';
import { DataTable } from '../../../../components/ui/DataTable';
import { CopyableText } from '../../../../components/ui/CopyableText';
import { DeliveryChips } from '../../../../components/deliveries/DeliveryChips';
import { DestinationCell } from '../../../../components/deliveries/DestinationCell';
import { StatusBadge } from '../../../../components/deliveries/StatusBadge';
import { ItemsCountCell } from '../../../../components/deliveries/ItemsCountCell';
import { DispatchToCourierModal } from '../../../../components/deliveries/DispatchToCourierModal';
import { ChangeCourierBulkModal } from '../../../../components/deliveries/ChangeCourierBulkModal';
import { getDeliveries } from '../../../../features/deliveries/deliveriesThunks';
import { setFilters } from '../../../../features/deliveries/deliveriesSlice';
import { useSelectedOrderIds } from '../../../../features/deliveries/useSelectedOrderIds';
import { formatCourier } from '../../../../features/deliveries/deliveriesUtils';
import { selectHasFulfillmentPro } from '../../../../features/auth/authSelectors';
import { EMPTY_ADVANCED_FILTER, isAdvancedFilterActive } from '../../../../features/deliveries/advancedFilter';
import { EDITABLE_ITEM_STATUSES } from '../../../../features/deliveryItems/deliveryItemsConstants';

import { DeliveryFilters } from './components/DeliveryFilters';
import { DeliveryActions } from './components/DeliveryActions';
import { SelectionActions } from '../../../../components/deliveries/SelectionActions';
import { ManageBultosModal } from '../../../../components/deliveries/ManageBultosModal';

// Filters the admin's screen has and this one does not. The slice is shared and
// persisted, so a value left there by an admin session would narrow this table
// with nothing on screen to show it or undo it. The advanced filter is one of
// them: it is the admin's alone (a seller never inherits the addon), and the
// backend refuses the whole listing (403) when a seller sends it.
const HIDDEN_FILTERS = {
  company: 'all',
  assignedDriverFilter: 'all',
  upsell_filter: 'all',
  ...EMPTY_ADVANCED_FILTER,
};

/**
 * The seller's shipments. `api/delivery-resume/` scopes the listing to the
 * seller from the session, so there is no seller column and no seller filter.
 */
export const Deliveries = () => {
  const dispatch = useDispatch();
  const { data, total, statusCount, globalTotal, globalStatusCount, isLoading, filters, error } =
    useSelector((state) => state.deliveries);
  const { user } = useSelector((state) => state.auth);
  const hasFulfillmentPro = useSelector(selectHasFulfillmentPro);

  const [selectedRows, setSelectedRows] = useState([]);
  const selectedOrderIds = useSelectedOrderIds(data, selectedRows);

  const [isDispatchOpen, setIsDispatchOpen] = useState(false);
  const [isChangeCourierOpen, setIsChangeCourierOpen] = useState(false);
  // The row whose bultos are being re-counted; one modal serves the whole table.
  const [itemsCountRow, setItemsCountRow] = useState(null);

  const { company, assignedDriverFilter, upsell_filter: upsellFilter } = filters;
  const hasHiddenFilters =
    [company, assignedDriverFilter, upsellFilter].some((value) => (value ?? 'all') !== 'all') ||
    isAdvancedFilterActive(filters);

  useEffect(() => {
    if (hasHiddenFilters) dispatch(setFilters({ ...HIDDEN_FILTERS, page: 1 }));
  }, [dispatch, hasHiddenFilters]);

  useEffect(() => {
    if (!hasHiddenFilters) dispatch(getDeliveries(filters));
  }, [dispatch, filters, hasHiddenFilters]);

  // Any filter change goes back to the first page; the page itself does not,
  // which is what makes the pager work.
  const handleFilterChange = (fieldOrUpdates, value) => {
    const patch = typeof fieldOrUpdates === 'object' && fieldOrUpdates !== null
      ? fieldOrUpdates
      : { [fieldOrUpdates]: value };
    dispatch(setFilters({ ...patch, page: 1 }));
  };

  const canEditItemsCount = (row) =>
    hasFulfillmentPro && EDITABLE_ITEM_STATUSES.includes(row.status) && !row.is_external;

  const columns = [
    {
      header: 'ID & INFO',
      cell: (row) => (
        <div className="flex flex-col gap-1">
          <Link
            to={`/order-details/${row.order_id}`}
            title="Ver Detalles"
            className="font-bold text-ninja-dark dark:text-white hover:text-ninja-green hover:underline transition-colors w-fit"
          >
            #{row.order_id}
          </Link>
          <div className="flex flex-col items-start gap-1 mt-1">
            {!row.has_label && (
              <div title="Sin etiqueta" className="flex items-center gap-1 bg-red-50 dark:bg-red-900/20 text-red-500 px-1.5 py-0.5 rounded text-[10px] font-bold border border-red-100 dark:border-red-900/50">
                <PrinterX size={12} />
                <span>SIN ETIQUETA</span>
              </div>
            )}
            {row.has_label && <span className="h-[20px]"></span>}
          </div>
        </div>
      )
    },
    {
      header: 'DETALLES',
      cell: (row) => (
        <div className="flex flex-col">
          <CopyableText
            value={row.reference_id}
            maxWidth="180px"
            wrap
            className="font-medium text-ninja-dark dark:text-white"
          />
          <ItemsCountCell
            count={row.total_items}
            editable={canEditItemsCount(row)}
            onEdit={() => setItemsCountRow(row)}
          />
        </div>
      )
    },
    {
      header: 'DESTINO',
      cell: (row) => <DestinationCell row={row} />
    },
    {
      header: 'LOGÍSTICA',
      cell: (row) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5 font-medium text-ninja-dark dark:text-white">
            <Truck size={14} className="text-text-muted" />
            <span>{formatCourier(row.courier)}</span>
          </div>
          <div className="mt-0.5">
            <CopyableText
              value={row.tracking_number}
              maxWidth="105px"
              className="text-xs text-ninja-green font-medium"
              emptyLabel="Sin tracking"
              emptyClassName="text-xs text-text-secondary"
            />
          </div>
        </div>
      )
    },
    {
      header: 'ESTADO',
      cell: (row) => <StatusBadge status={row.status} testId="delivery-status" />
    },
    // Apart from the status: the date is the order's, and stacked under a badge
    // that moves on its own it read as the date of the status.
    {
      header: 'CREADA',
      cell: (row) => (
        <span className="text-xs font-medium text-text-secondary dark:text-gray-400">{row.created_at || '-'}</span>
      )
    }
  ];

  return (
    <div data-testid="deliveries-page" className="space-y-6">
      <DeliveryChips
        total={globalTotal > 0 ? globalTotal : total}
        statusCount={globalStatusCount?.length > 0 ? globalStatusCount : statusCount}
        currentStatus={filters.delivery_status}
        onStatusChange={(status) => handleFilterChange('delivery_status', status)}
      />

      <DeliveryFilters filters={filters} onFilterChange={handleFilterChange} />

      <div className="flex flex-col gap-4 mt-6">
        <div className="min-h-[48px] flex items-center justify-between transition-all duration-300">
          <h2 className="text-base font-bold text-ninja-dark dark:text-white">
            Ordenes
          </h2>

          <DeliveryActions
            user={user}
            filters={filters}
          />
        </div>

        <SelectionActions
          user={user}
          selectedRows={selectedRows}
          selectedOrderIds={selectedOrderIds}
          canDispatch={hasFulfillmentPro}
          canChangeCourier={hasFulfillmentPro}
          onOpenDispatchModal={() => setIsDispatchOpen(true)}
          onOpenChangeCourierModal={() => setIsChangeCourierOpen(true)}
          onFulfilled={() => {
            dispatch(getDeliveries(filters));
            setSelectedRows([]);
          }}
        />

        <DataTable
          columns={columns}
          data={data}
          totalItems={total}
          currentPage={filters.page}
          itemsPerPage={filters.rowsPerPage}
          isLoading={isLoading}
          onPageChange={(page) => dispatch(setFilters({ page }))}
          onRowsPerPageChange={(rowsPerPage) => dispatch(setFilters({ rowsPerPage, page: 1 }))}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          idAccessor="delivery_id"
          error={error}
          onRetry={() => dispatch(getDeliveries(filters))}
        />
      </div>

      <DispatchToCourierModal
        isOpen={isDispatchOpen}
        onClose={() => setIsDispatchOpen(false)}
        deliveryIds={selectedRows}
        onFinished={(failedIds) => {
          dispatch(getDeliveries(filters));
          // What did not go out stays selected, ready for a retry.
          setSelectedRows(failedIds);
        }}
      />

      {/* The couriers offered are its operator's: the backend resolves a seller to its admin. */}
      <ChangeCourierBulkModal
        isOpen={isChangeCourierOpen}
        onClose={() => setIsChangeCourierOpen(false)}
        deliveryIds={selectedRows}
        onFinished={(failedIds) => {
          dispatch(getDeliveries(filters));
          setSelectedRows(failedIds);
        }}
      />

      {/* Count and, with Fulfillment Pro, the logistics of each bulto: the
          same rules as the order detail's "Bultos" tab, without leaving the list. */}
      <ManageBultosModal
        isOpen={itemsCountRow !== null}
        onClose={() => setItemsCountRow(null)}
        row={itemsCountRow}
      />
    </div>
  );
};
